// ClubWise vocabulary: which Status values mean "joined", and which Source / Contact
// Type spellings mean Meta, Website or Staff generated. CONFIG, NOT INLINE CODE
// (Scott): this is the one file to edit when a club's export surfaces a new spelling.
//
// ⚠️ Everything is matched on a NORMALISED value (classify.ts normaliseForMatch):
// lowercase, whitespace collapsed, a numbering prefix and a year removed, PII blanked.
// ⚠️ Nothing here is a closed vocabulary. An unlisted value is never rejected: it
// lands in OTHER and the upload preview lists it with its count.

/**
 * ⚠️ THE AU BRAND KEY ONLY, kept for AU's own rows. Anything that must serve more
 * than one brand takes the brand's key from the registry (brands.ts), never this.
 */
export const CLUBWISE_BRAND_KEY = 'anytime_fitness_au';

/** Cell values that mean "not recorded". Compared after trimming. */
export const NOT_RECORDED_VALUES: readonly string[] = ['none'];

/** Statuses on a person who JOINED. */
export const JOINED_STATUSES: readonly string[] = ['not setup', 'term at club', 'frozen'];
/** Statuses on a person who did NOT join. */
export const NOT_JOINED_STATUSES: readonly string[] = ['blow out', 'hot prospect', 'prospect', 'guest'];

/** UK statuses (2026-10-30, Scott). The UK export uses a different joined vocabulary. */
export const UK_JOINED_STATUSES: readonly string[] = [
  'new to club',
  'under contract',
  'in arrears',
  'confirm dd',
  'term at club',
];
export const UK_NOT_JOINED_STATUSES: readonly string[] = ['blow out', 'hot prospect', 'prospect', 'guest'];

/** Contact Type values (normalised) that mean "Web joiner": HOW they joined, not where the lead came from. UK only. */
export const WEB_JOINER_CONTACT_PATTERNS: readonly RegExp[] = [/^web joiner$/];

/** UK web joiners, by SOURCE: staff-run sources. Inreach, Outreach, Pos Referral. */
export const UK_WEB_JOINER_STAFF_SOURCE_PATTERNS: readonly RegExp[] = [
  /\binreach\b/,
  /\boutreach\b/,
  /\bpos referral\b/,
];

// ---------------------------------------------------------------------------
// CHANNELS (2026-10-20, Scott): every lead is Meta, Website, Staff generated or Other.
//   · LT (Lt lead / Lt Network ...) = Meta. The apps (Af app, Anytime App ...) = Website.
//   · Staff always wins; Meta wins over Website; anything else is Other.
//   · `app` is matched as a WHOLE WORD, so "Appointment" is not an app.
// ---------------------------------------------------------------------------

/** Contact Type only: staff generated. Staff always wins over a digital Source. */
export const STAFF_CONTACT_PATTERNS: readonly RegExp[] = [
  /\bstaff generated\b/,
  /\binreach\b/,
  /\boutreach\b/,
  /\bpos referral\b/,
  /^pos$/,
];

/** Either field: Meta (Facebook, Instagram, TikTok, the LT network, paid-social contact types). */
export const META_PATTERNS: readonly RegExp[] = [
  /\bfacebook\b/,
  /\bfb\b/,
  /\binstagram\b/,
  /\binsta\b/,
  /\bmeta\b/,
  /\bsocial media\b/,
  /\btiktok\b/,
  /^lt\b/,
  /^digital leads?$/,
  /^digital ads?$/,
  /^lead ?gen(eration)?$/,
  /^growth ?ops$/,
];

/** Either field: Website (web forms, landing pages, search, the apps). */
export const WEBSITE_PATTERNS: readonly RegExp[] = [
  /\bweb\b/,
  /\bwebsite\b/,
  /\bafws\b/,
  /\bpurposebrands\b/,
  /\bpromotional page\b/,
  /\bmember enq\b/,
  /\bgoogle\b/,
  /\binternet\b/,
  /\bonline\b/,
  /\bsem\b/,
  /\bapp\b/,
];

/** A value with this many digits is a phone number (or worse) and is treated as blank. */
export const PII_MIN_DIGITS = 8;

/** Years stripped from the start or end of a value ("2019 social media"). */
export const YEAR_PATTERN = '(?:201[5-9]|202[0-9]|2030)';

/** What a blank Source on a website / meta contact is called in the breakdown. */
export const UNTAGGED_WEBSITE_LABEL = 'Website (untagged)';
export const UNTAGGED_META_LABEL = 'Meta (untagged)';
